'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import ResearchMenuIcon from '../Icons/ResearchMenuIcon';
import AlfaGptMenuIcon from '../Icons/AlfaGptMenuIcon';
import { styled, ToggleButton, ToggleButtonGroup, toggleButtonGroupClasses } from '@mui/material';
import { ChatMode, useChatMode } from '@/contexts/ChatModeContext';
import { useRouter, useSearchParams } from 'next/navigation';
import { Brain } from 'lucide-react';
import StrategyLabIcon from '../Icons/StrategyLabIcon';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`.${toggleButtonGroupClasses.root}`]: {
    borderRadius: 999,
  },
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    padding: 0,
    border: 0,
    borderRadius: 999,
  },
}));
interface DeepSearchProps {
  enabled?: boolean;
  onChange?: (enabled: boolean) => void;
  isSmall?: boolean;
}

const DeepSearchSwitch: React.FC<DeepSearchProps> = ({ enabled = false, onChange, isSmall }) => {
  // const [isEnabled, setIsEnabled] = useState(enabled);
  // const [llm, setLlm] = useState<'alfa' | 'research' | 'gpt'>('alfa');
  const router = useRouter();
  const searchParams = useSearchParams();
  const { changeMode, agent, mode, changeAgent } = useChatMode();
  const chatId = searchParams.get('chat_id');

  const newChat = () => {
    if (chatId) {
      const url = new URL(location.href);
      url.searchParams.delete('chat_id');
      router.push(url.href);
    }
  };

  const handleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: ChatMode) => {
    changeMode(newAlignment);
    if (newAlignment === 'alfa') {
      changeAgent('alfa');
    } else if (newAlignment === 'research') {
      changeAgent('research');
    } else if (newAlignment === 'strategy') {
      changeAgent('sla');
    } else {
      changeAgent('gpt-5');
    }
    newChat();
  };

  return (
    <div className="flex items-center gap-2">
      <StyledToggleButtonGroup
        orientation="horizontal"
        value={mode}
        exclusive
        onChange={handleChange}
        className={cn(
          'bg-bg-card relative inline-flex gap-2! p-0.5 items-center rounded-full! transition-colors duration-200 border border-[#2A2A2A]',
          'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
          enabled ? 'bg-[#523BCD]' : '',
        )}
        classes={{ root: cn('') }}
      >
        <ToggleButton value="alfa" aria-label="alfa">
          <span
            className={cn(
              'inline-flex items-center justify-center',
              'transform transition-transform duration-200',
              'size-[26px] rounded-full text-gray-400',
              {
                'text-[#523BCD] bg-white': mode === 'alfa',
              },
            )}
          >
            <AlfaGptMenuIcon
              className={cn('h-5 w-5 transition-colors')}
              aiColor={mode === 'alfa' ? '#523BCD' : '#9ca3af'}
            />
          </span>
        </ToggleButton>
        <ToggleButton value="research" aria-label="research">
          <span
            className={cn(
              'inline-flex items-center justify-center',
              'transform transition-transform duration-200',
              'size-[26px] rounded-full text-gray-400',
              {
                'text-[#523BCD] bg-white': mode === 'research',
              },
            )}
          >
            <ResearchMenuIcon className={cn('h-5 w-5 transition-colors')} />
          </span>
        </ToggleButton>
        <ToggleButton value="strategy" aria-label="strategy">
          <span
            className={cn(
              'inline-flex items-center justify-center',
              'transform transition-transform duration-200',
              'size-[26px] rounded-full text-gray-400',
              {
                'text-[#523BCD] bg-white': mode === 'strategy',
              },
            )}
          >
            <StrategyLabIcon className={cn('h-5 w-5 transition-colors')} />
          </span>
        </ToggleButton>
        <ToggleButton value="brain" aria-label="gpt">
          <span
            className={cn(
              'inline-flex items-center justify-center',
              'transform transition-transform duration-200',
              'size-[26px] rounded-full text-gray-400',
              {
                'text-[#523BCD] bg-white': mode === 'brain',
              },
            )}
          >
            <Brain className={cn('h-5 w-5 transition-colors')} />
          </span>
        </ToggleButton>
      </StyledToggleButtonGroup>
    </div>
  );
};

export default DeepSearchSwitch;
